import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useCompany } from "../../../contexts/CompanyContext";

interface Announcement {
  id: string;
  title: string;
  bodyText: string;
  urgency: "normal" | "important" | "urgent";
  created_at: string;
  isRead: boolean;
}

const URGENCY_CONFIG = {
  normal: { label: "일반", color: "#7A9BFF", bg: "rgba(122,155,255,0.08)", border: "rgba(122,155,255,0.2)", icon: "ri-notification-line" },
  important: { label: "중요", color: "#FF9F43", bg: "rgba(255,159,67,0.08)", border: "rgba(255,159,67,0.2)", icon: "ri-alarm-warning-line" },
  urgent: { label: "긴급", color: "#E8375F", bg: "rgba(232,55,95,0.08)", border: "rgba(232,55,95,0.2)", icon: "ri-error-warning-fill" },
};

const READ_KEY = "kap_read_announcements";

function getReadIds(): Set<string> {
  try {
    const raw = localStorage.getItem(READ_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

function markRead(id: string) {
  const ids = getReadIds();
  ids.add(id);
  localStorage.setItem(READ_KEY, JSON.stringify([...ids]));
}

export default function AnnouncementList() {
  const { workspaceId } = useCompany();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Announcement | null>(null);
  const [readIds, setReadIds] = useState<Set<string>>(getReadIds);

  const fetchAnnouncements = useCallback(async () => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);

    const { data } = await supabase
      .from("role_notifications")
      .select("id, title, body, ref_id, created_at")
      .eq("workspace_id", workspaceId)
      .eq("type", "announcement")
      .order("created_at", { ascending: false });

    const currentRead = getReadIds();
    const rows: Announcement[] = (data ?? []).map(row => {
      let bodyText = "";
      let urgency: "normal" | "important" | "urgent" = "normal";
      try {
        const p = JSON.parse(row.body);
        bodyText = p.text ?? "";
        urgency = (p.urgency ?? row.ref_id ?? "normal") as typeof urgency;
      } catch {
        bodyText = row.body ?? "";
        urgency = (row.ref_id ?? "normal") as typeof urgency;
      }
      return {
        id: row.id,
        title: row.title,
        bodyText,
        urgency: ["normal", "important", "urgent"].includes(urgency) ? urgency : "normal",
        created_at: row.created_at,
        isRead: currentRead.has(row.id),
      };
    });

    setAnnouncements(rows);
    setReadIds(currentRead);
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);

  const handleSelect = (ann: Announcement) => {
    markRead(ann.id);
    const updated = new Set(readIds);
    updated.add(ann.id);
    setReadIds(updated);
    setSelected({ ...ann, isRead: true });
    setAnnouncements(prev => prev.map(a => a.id === ann.id ? { ...a, isRead: true } : a));
  };

  const unreadCount = announcements.filter(a => !a.isRead).length;

  // Realtime 구독
  useEffect(() => {
    if (!workspaceId) return;
    const ch = supabase
      .channel(`announce-${workspaceId}`)
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "role_notifications",
        filter: `workspace_id=eq.${workspaceId}`,
      }, (payload) => {
        const row = payload.new as { type: string; id: string; title: string; body: string; ref_id: string; created_at: string };
        if (row.type !== "announcement") return;
        let bodyText = "";
        let urgency: "normal" | "important" | "urgent" = "normal";
        try {
          const p = JSON.parse(row.body);
          bodyText = p.text ?? "";
          urgency = (p.urgency ?? row.ref_id ?? "normal") as typeof urgency;
        } catch {
          bodyText = row.body ?? "";
        }
        const newAnn: Announcement = {
          id: row.id, title: row.title, bodyText, urgency, created_at: row.created_at, isRead: false,
        };
        setAnnouncements(prev => [newAnn, ...prev]);
      }).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [workspaceId]);

  return (
    <div className="flex h-full">
      {/* 목록 */}
      <div className="flex flex-col border-r border-white/[0.05]" style={{ width: 270 }}>
        <div className="px-4 py-3 border-b border-white/[0.05] flex items-center justify-between shrink-0">
          <div>
            <span className="text-[#F2F2F2] text-sm font-medium">공지사항</span>
            {unreadCount > 0 && (
              <span className="ml-2 bg-[#FF9F43]/20 text-[#FF9F43] text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <button type="button" onClick={fetchAnnouncements}
            className="w-6 h-6 flex items-center justify-center text-[#3D3D3D] hover:text-[#777777] cursor-pointer transition-colors">
            <i className="ri-refresh-line text-xs"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-16 bg-white/[0.02] rounded-lg animate-pulse" />
              ))}
            </div>
          ) : announcements.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <i className="ri-megaphone-line text-2xl text-[#222222] block mb-3"></i>
              <p className="text-[#3D3D3D] text-xs leading-relaxed">
                {workspaceId ? "아직 공지사항이 없어요" : "워크스페이스에 입장한 후\n공지를 확인할 수 있어요"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.03]">
              {announcements.map(ann => {
                const cfg = URGENCY_CONFIG[ann.urgency] ?? URGENCY_CONFIG.normal;
                const isSelected = selected?.id === ann.id;
                return (
                  <button
                    key={ann.id}
                    type="button"
                    onClick={() => handleSelect(ann)}
                    className={`w-full text-left px-4 py-3 transition-colors cursor-pointer ${
                      isSelected ? "bg-white/[0.05]" : "hover:bg-white/[0.02]"
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      {!ann.isRead && (
                        <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ backgroundColor: cfg.color }} />
                      )}
                      {ann.isRead && <div className="w-1.5 h-1.5 shrink-0 mt-1.5" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <i className={`${cfg.icon} text-xs shrink-0`} style={{ color: cfg.color }}></i>
                          <span
                            className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full border shrink-0"
                            style={{ color: cfg.color, borderColor: `${cfg.color}30`, backgroundColor: `${cfg.color}10` }}
                          >
                            {cfg.label}
                          </span>
                        </div>
                        <p className={`text-xs font-medium leading-snug truncate ${ann.isRead ? "text-[#888888]" : "text-[#F2F2F2]"}`}>
                          {ann.title}
                        </p>
                        <p className="text-[#444444] text-[10px] truncate mt-0.5 leading-snug">{ann.bodyText}</p>
                        <p className="text-[#333333] text-[9px] mt-1">
                          {new Date(ann.created_at).toLocaleDateString("ko-KR", { month: "long", day: "numeric" })}
                          {" · "}
                          {new Date(ann.created_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 상세 */}
      <div className="flex-1 flex flex-col overflow-hidden bg-[#0C0C0E]">
        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/[0.03] border border-white/[0.05]">
              <i className="ri-megaphone-line text-2xl text-[#333333]"></i>
            </div>
            <div className="text-center">
              <p className="text-[#555555] text-sm">공지를 선택해 내용을 확인하세요</p>
              {unreadCount > 0 && (
                <p className="text-[#FF9F43]/70 text-xs mt-1">읽지 않은 공지 {unreadCount}건</p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6">
            {(() => {
              const cfg = URGENCY_CONFIG[selected.urgency] ?? URGENCY_CONFIG.normal;
              return (
                <>
                  {/* 긴급도 배지 */}
                  <div className="flex items-center gap-2 mb-4">
                    <span
                      className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-full border"
                      style={{ color: cfg.color, borderColor: `${cfg.color}30`, backgroundColor: `${cfg.color}10` }}
                    >
                      <i className={`${cfg.icon} text-sm`}></i>
                      {cfg.label} 공지
                    </span>
                    <span className="text-[#333333] text-[10px]">
                      {new Date(selected.created_at).toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" })}
                      {" "}
                      {new Date(selected.created_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>

                  {/* 제목 */}
                  <h2 className="text-[#F2F2F2] font-bold text-lg leading-snug mb-4">{selected.title}</h2>

                  {/* 구분선 */}
                  <div
                    className="h-px w-full mb-5"
                    style={{ background: `linear-gradient(90deg, ${cfg.color}30, transparent)` }}
                  />

                  {/* 본문 */}
                  <div
                    className="rounded-xl border p-5 mb-4"
                    style={{ borderColor: cfg.border, backgroundColor: cfg.bg }}
                  >
                    <pre className="text-[#CCCCCC] text-sm leading-relaxed whitespace-pre-wrap font-sans">
                      {selected.bodyText}
                    </pre>
                  </div>

                  {/* 발신자 정보 */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.02] rounded-lg border border-white/[0.04]">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gradient-to-br from-[#E8375F] to-[#FF9F43]">
                      <i className="ri-user-line text-white text-xs"></i>
                    </div>
                    <div>
                      <div className="text-[#888888] text-[10px] font-semibold">강사</div>
                      <div className="text-[#444444] text-[9px]">워크스페이스 관리자 발송</div>
                    </div>
                    <div className="ml-auto">
                      <span className="text-[9px] text-emerald-400/60 flex items-center gap-1">
                        <i className="ri-eye-line text-xs"></i>읽음
                      </span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
